import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "frozenOffsets", "zebraStripe", "lastColumnWidth", "minWidth", "condensed", "dataDensity", "callToActionColumnIndex", "callToActionHighlightColor"];
import { c as _c } from "react-compiler-runtime";
import styled, { isTrellisTheme } from 'foundations-theming/utils';
import { fwcColor } from 'foundations-theming/utils/fwc';
import { CELL_SELECTED_ATTRIBUTE, FILL_HANDLE_ATTRIBUTE, CELL_EDGES_ATTRIBUTE } from '../constants/cellSelectionAttributes';
import { TABLE_TOP_BAR_HEIGHT, TABLE_PAGINATOR_HEIGHT, TABLE_NON_EMPTY_MIN_CONTENT_HEIGHT, SELECTION_COLUMN_WIDTH, TABLE_ROW_HEIGHT_BY_DENSITY } from '../constants/sizes';
import { forwardRef, useCallback } from 'react';
import { ScrollOverhang } from 'foundations-components/transitional/components/ScrollOverhang';
import { View } from 'foundations-components/components/View';
import { Table } from 'foundations-components/transitional/components/Table';
import { ProvideScrollWindowRef, useScrollWindowRef } from '../context/ScrollWindowRefContext';
import { mergeRefs } from '../../utils/mergeRefs';
import { jsx as _jsx } from "react/jsx-runtime";
const ScrollWindowPresentational = styled.div.withConfig({
  displayName: "StyledTableComponents__ScrollWindowPresentational"
})(["overflow:auto;position:relative;max-width:100%;max-height:100%;", ""], ({
  $rightToolbarWidth
}) => $rightToolbarWidth ? `margin-inline-end: ${$rightToolbarWidth}px;` : '');

// This is helpful if you would like to style an element as the full width of the scroll container (as opposed to the full width of the <table> element)
export const FRAMEWORK_DATA_TABLE_SCROLL_CSS_CONTAINER_NAME = 'FRAMEWORK_DATA_TABLE_SCROLL_CSS_CONTAINER_NAME';
const TableScrollContainerWrapper = styled.div.withConfig({
  displayName: "StyledTableComponents__TableScrollContainerWrapper"
})(["display:flex;flex-direction:column;inline-size:100%;block-size:100%;min-inline-size:0;flex-grow:0;container-type:inline-size;container-name:", ";"], FRAMEWORK_DATA_TABLE_SCROLL_CSS_CONTAINER_NAME);
const ScrollWindow = /*#__PURE__*/forwardRef((t0, ref) => {
  const $ = _c(7);
  const {
    children,
    rightToolbarWidth
  } = t0;
  const {
    setElement
  } = useScrollWindowRef();
  let t1;
  if ($[0] !== ref || $[1] !== setElement) {
    t1 = node => mergeRefs([ref, setElement])(node);
    $[0] = ref;
    $[1] = setElement;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const mergedRef = t1;
  let t2;
  if ($[3] !== children || $[4] !== mergedRef || $[5] !== rightToolbarWidth) {
    t2 = /*#__PURE__*/_jsx(ScrollWindowPresentational, {
      ref: mergedRef,
      "data-test-id": "fdt-scroll-window",
      $rightToolbarWidth: rightToolbarWidth,
      children: children
    });
    $[3] = children;
    $[4] = mergedRef;
    $[5] = rightToolbarWidth;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
});
const FDT_BORDER_COLOR_TOKEN = 'border-core-subtle';
// The ScrollOverhang component adds `flex: 1 1 auto` to its wrapper, which causes content overflow.
// This Flex container prevents that by using column layout with specific constraints (minInlineSize="0"
// and flexGrow={0}) to maintain proper scrolling behavior without expanding beyond its boundaries.
// See: https://git.hubteam.com/HubSpot/foundations-components/issues/3950
export const TableScrollContainer = /*#__PURE__*/forwardRef((t0, ref) => {
  const $ = _c(4);
  const {
    children,
    rightToolbarWidth
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== ref || $[2] !== rightToolbarWidth) {
    t1 = /*#__PURE__*/_jsx(TableScrollContainerWrapper, {
      children: /*#__PURE__*/_jsx(ScrollOverhang, {
        children: /*#__PURE__*/_jsx(ProvideScrollWindowRef, {
          children: /*#__PURE__*/_jsx(ScrollWindow, {
            ref: ref,
            rightToolbarWidth: rightToolbarWidth,
            children: children
          })
        })
      })
    });
    $[0] = children;
    $[1] = ref;
    $[2] = rightToolbarWidth;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
});
export function TableBox(props) {
  const $ = _c(11);
  const {
    loading,
    showPagination,
    showTopBar,
    hasTopBorderRadius,
    borderless
  } = props;
  const maxBlockSize = `calc(100% ${showTopBar ? ` - ${TABLE_TOP_BAR_HEIGHT}` : ""} ${showPagination ? ` - ${TABLE_PAGINATOR_HEIGHT}px` : ""})`;
  const minBlockSize = loading ? `${TABLE_NON_EMPTY_MIN_CONTENT_HEIGHT}px` : undefined;
  if (borderless) {
    let t0;
    if ($[0] !== maxBlockSize || $[1] !== minBlockSize || $[2] !== props.children) {
      t0 = /*#__PURE__*/_jsx(View, {
        inlineSize: "100%",
        flexGrow: 1,
        maxBlockSize: maxBlockSize,
        minBlockSize: minBlockSize,
        position: "relative",
        overflow: "hidden",
        children: props.children
      });
      $[0] = maxBlockSize;
      $[1] = minBlockSize;
      $[2] = props.children;
      $[3] = t0;
    } else {
      t0 = $[3];
    }
    return t0;
  }
  let t0;
  if ($[4] !== hasTopBorderRadius) {
    t0 = hasTopBorderRadius ? {
      borderStartStartRadius: "300",
      borderStartEndRadius: "300"
    } : {};
    $[4] = hasTopBorderRadius;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  const topRadiusProps = t0;
  let t1;
  if ($[6] !== maxBlockSize || $[7] !== minBlockSize || $[8] !== props.children || $[9] !== topRadiusProps) {
    t1 = /*#__PURE__*/_jsx(View, Object.assign({
      borderColor: FDT_BORDER_COLOR_TOKEN,
      borderStyle: "solid",
      borderWidth: "100",
      inlineSize: "100%",
      maxBlockSize: maxBlockSize,
      minBlockSize: minBlockSize,
      position: "relative",
      overflow: "hidden",
      borderEndStartRadius: "300",
      borderEndEndRadius: "300"
    }, topRadiusProps, {
      children: props.children
    }));
    $[6] = maxBlockSize;
    $[7] = minBlockSize;
    $[8] = props.children;
    $[9] = topRadiusProps;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}
export const STYLED_TABLE_COMPONENTS_FROZEN_CELL_Z_INDEX = 2;
export const THEAD_Z_INDEX = 4;

/**
 * This is only exported for testing purposes - dont consume this external to this file
 */
export const getTableContainerBlockSize = ({
  $dataDensity,
  $condensed,
  theme
}) => {
  if ($dataDensity) {
    var _TABLE_ROW_HEIGHT_BY_;
    const {
      spaceToken
    } = (_TABLE_ROW_HEIGHT_BY_ = TABLE_ROW_HEIGHT_BY_DENSITY[$dataDensity]) !== null && _TABLE_ROW_HEIGHT_BY_ !== void 0 ? _TABLE_ROW_HEIGHT_BY_ : TABLE_ROW_HEIGHT_BY_DENSITY.medium;
    return `block-size: ${theme.space[spaceToken]};`;
  }
  return $condensed ? `block-size: ${theme.space[TABLE_ROW_HEIGHT_BY_DENSITY.medium.spaceToken]};` : `min-block-size: ${theme.space[TABLE_ROW_HEIGHT_BY_DENSITY.medium.spaceToken]};`;
};
const TableContainer = styled.div.withConfig({
  displayName: "StyledTableComponents__TableContainer"
})(["--standard-border-width:", ";--heavy-border-width:", ";--border-color:", ";& > table{border-width:0;table-layout:fixed;min-inline-size:", "px;border-collapse:separate;border-spacing:0;", " & > thead > tr > th[data-selection-column='true'],& > tbody > tr > td[data-selection-column='true']{inline-size:", "px;padding:0;", "}& > thead{position:sticky;inset-block-start:0;z-index:", ";& > tr > [data-floating-ui-focus-guard]{display:none;}& > tr[data-row-type='select-query-row']{background-color:", ";block-size:", ";& > td{border-bottom:var(--standard-border-width) solid var(--border-color);&[data-cell-type='select-query']{overflow:visible;white-space:nowrap;", ";}}}& > tr > th{border-width:0;padding:0;border-bottom:var(--standard-border-width) solid var(--border-color);opacity:1;overflow-wrap:anywhere;word-break:normal;&:last-of-type:not([data-right-toolbar-column='true']),&:has(+ th[data-right-toolbar-column='true']){min-width:", "px;width:auto;}&:first-of-type{border-left-width:0;}}}& > tbody table{& > thead{z-index:", ";}}& > tbody{& > tr > td{", " padding-block:", ";line-height:inherit;border-width:0;border-left:var(--standard-border-width) solid transparent;border-bottom:var(--standard-border-width) solid var(--border-color);background-color:", ";&:first-of-type{border-left-width:0;}&[data-editable-cell='true']{cursor:text;[data-pressed='true']{box-shadow:inset 0 0 0 ", " ", ";}[data-invalid='true']{box-shadow:inset 0 0 0 ", " ", ";}&:hover,&:focus{box-shadow:inset 0 0 0 ", " ", ";}}}& > tr > td:where(.streaming-cell-flash){position:relative;overflow:hidden;}& > tr > td.streaming-cell-flash::before{content:'';position:absolute;inset:0;background:linear-gradient( 90deg,transparent 0%,", " 50%,transparent 100% );opacity:0;transform:translateX(-100%);animation:streaming-cell-sweep 1.4s ease-in-out forwards;}& > tr > td.streaming-cell-flash::after{content:'';position:absolute;inset:0;border:var(--standard-border-width) solid ", ";opacity:0;animation:streaming-cell-border 1.4s ease-out forwards;}@keyframes streaming-cell-sweep{0%{transform:translateX(-100%);opacity:0;}10%{opacity:0.12;}50%{opacity:0.12;}90%{opacity:0;}100%{transform:translateX(100%);opacity:0;}}@keyframes streaming-cell-border{0%{opacity:0;}15%{opacity:0.45;}50%{opacity:0.3;}100%{opacity:0;}}& > tr{& > [data-floating-ui-focus-guard]{display:none;}", " &:hover{& > td{background-color:", ";}}&:last-of-type{& > td{border-bottom-width:0;}}}}& > tbody > tr > td[", "],& > tbody > tr:hover > td[", "]{--fdt-et:0;--fdt-er:0;--fdt-eb:0;--fdt-el:0;position:sticky;user-select:none;}& > tbody > tr > td[", "]::before,& > tbody > tr:hover > td[", "]::before{content:'';position:absolute;inset-block-start:0;inset-block-end:calc(-1 * var(--standard-border-width));inset-inline-start:calc(-1 * var(--standard-border-width));inset-inline-end:0;border-style:solid;border-color:", ";border-top-width:calc(var(--fdt-et) * var(--standard-border-width));border-right-width:calc(var(--fdt-er) * var(--standard-border-width));border-bottom-width:calc(var(--fdt-eb) * var(--standard-border-width));border-left-width:calc(var(--fdt-el) * var(--standard-border-width));pointer-events:none;}& > tbody > tr > td[", "]:first-of-type::before,& > tbody > tr:hover > td[", "]:first-of-type::before{inset-inline-start:0;}& > tbody > tr > td[", "][", "*='t'],& > tbody > tr:hover > td[", "][", "*='t']{--fdt-et:1;}& > tbody > tr > td[", "][", "*='r'],& > tbody > tr:hover > td[", "][", "*='r']{--fdt-er:1;}& > tbody > tr > td[", "][", "*='b'],& > tbody > tr:hover > td[", "][", "*='b']{--fdt-eb:1;}& > tbody > tr > td[", "][", "*='l'],& > tbody > tr:hover > td[", "][", "*='l']{--fdt-el:1;}& > tbody > tr > td[", "]{position:relative;}& > tbody > tr > td[", "]::after{content:'';position:absolute;inset-inline-end:-3px;inset-block-end:-3px;inline-size:10px;block-size:10px;border-radius:50%;background-color:", ";border:1px solid ", ";cursor:crosshair;z-index:1;}", " ", "}"], ({
  theme
}) => theme.borderWidth['100'], ({
  theme
}) => theme.borderWidth['300'], ({
  theme
}) => theme.color[FDT_BORDER_COLOR_TOKEN], ({
  minWidth
}) => minWidth, ({
  frozenOffsets
}) => frozenOffsets === null || frozenOffsets === void 0 ? void 0 : frozenOffsets.map((offset, index) => `
      & > thead > tr > th[data-column-index='${index}'], & > tbody > tr > td[data-column-index='${index}'] {
        position: sticky;
        z-index: ${STYLED_TABLE_COMPONENTS_FROZEN_CELL_Z_INDEX};
        inset-inline-start: ${offset}px;

        /* Set heavy border on last frozen column */
        ${index === frozenOffsets.length - 1 && `
          border-right-width: var(--heavy-border-width);

          /* Disable left border on the column directly after the last frozen column
          (otherwise it stacks with the heavy border) */
          & + td, & + th {
            border-left-width: 0;
          }
        `}
      }
      `), SELECTION_COLUMN_WIDTH, ({
  frozenOffsets
}) => frozenOffsets !== null && frozenOffsets !== void 0 && frozenOffsets.length ? `
          inset-inline-start: 0;
          position: sticky;
          z-index: ${STYLED_TABLE_COMPONENTS_FROZEN_CELL_Z_INDEX};
        ` : '', THEAD_Z_INDEX, ({
  theme
}) => theme.color['fill-info-subtle'], ({
  theme
}) => theme.space['1000'], ({
  frozenOffsets
}) => frozenOffsets !== null && frozenOffsets !== void 0 && frozenOffsets.length ? `position: sticky; inset-inline-start: 0;` : '', ({
  lastColumnWidth
}) => lastColumnWidth, THEAD_Z_INDEX - 1, ({
  $dataDensity,
  $condensed,
  theme
}) => getTableContainerBlockSize({
  $dataDensity,
  $condensed,
  theme
}), ({
  theme
}) => theme.space['50'], ({
  theme
}) => theme.color['fill-surface-default'], ({
  theme
}) => theme.space['25'], ({
  theme
}) => theme.color['fill-info-default'], ({
  theme
}) => theme.space['25'], ({
  theme
}) => theme.color['fill-alert-default'], ({
  theme
}) => theme.space['25'], ({
  theme
}) => theme.color['border-core-hover'], ({
  theme
}) => theme.color['border-accent-blue-default'], ({
  theme
}) => theme.color['border-accent-blue-default'], ({
  theme,
  zebraStripe = false
}) => {
  if (zebraStripe) {
    return `
          &[data-fdt-row-parity="even"] > td {
            background-color: ${theme.color['specialty-table-zebra-stripe-default']};
          }
          `;
  }
}, ({
  theme
}) => isTrellisTheme(theme) ? theme.color['fill-accent-neutral-subtle'] : theme.color['fill-field-hover'], CELL_SELECTED_ATTRIBUTE, CELL_SELECTED_ATTRIBUTE, CELL_SELECTED_ATTRIBUTE, CELL_SELECTED_ATTRIBUTE, ({
  theme
}) => theme.color[fwcColor('fwc-button-fill-primary-default')], CELL_SELECTED_ATTRIBUTE, CELL_SELECTED_ATTRIBUTE, CELL_SELECTED_ATTRIBUTE, CELL_EDGES_ATTRIBUTE, CELL_SELECTED_ATTRIBUTE, CELL_EDGES_ATTRIBUTE, CELL_SELECTED_ATTRIBUTE, CELL_EDGES_ATTRIBUTE, CELL_SELECTED_ATTRIBUTE, CELL_EDGES_ATTRIBUTE, CELL_SELECTED_ATTRIBUTE, CELL_EDGES_ATTRIBUTE, CELL_SELECTED_ATTRIBUTE, CELL_EDGES_ATTRIBUTE, CELL_SELECTED_ATTRIBUTE, CELL_EDGES_ATTRIBUTE, CELL_SELECTED_ATTRIBUTE, CELL_EDGES_ATTRIBUTE, FILL_HANDLE_ATTRIBUTE, FILL_HANDLE_ATTRIBUTE, ({
  theme
}) => theme.color[fwcColor('fwc-button-fill-primary-default')], ({
  theme
}) => theme.color['fill-surface-default'], ({
  frozenOffsets
}) => frozenOffsets === null || frozenOffsets === void 0 ? void 0 : frozenOffsets.map((_offset, index) => `
      & > tbody > tr > td[data-column-index='${index}'][${FILL_HANDLE_ATTRIBUTE}] {
        position: sticky;
      }
    `), ({
  $callToActionColumnIndex,
  $callToActionHighlightColor,
  theme
}) => {
  if ($callToActionColumnIndex == null || $callToActionHighlightColor == null) {
    return '';
  }
  const resolvedColor = theme.color[$callToActionHighlightColor];
  return `
      & > thead > tr > th[data-column-index='${$callToActionColumnIndex}'],
      & > tbody > tr > td[data-column-index='${$callToActionColumnIndex}'],
      & > tbody > tr:hover > td[data-column-index='${$callToActionColumnIndex}'],
      & > tbody > tr[data-fdt-row-parity='even'] > td[data-column-index='${$callToActionColumnIndex}'] {
        background-color: ${resolvedColor};
      }
    `;
});
export const StyledTable = t0 => {
  const $ = _c(25);
  let children;
  let condensed;
  let dataDensity;
  let frozenOffsets;
  let lastColumnWidth;
  let minWidth;
  let rest;
  let t1;
  let t2;
  let zebraStripe;
  if ($[0] !== t0) {
    var _t = t0;
    ({
      children,
      frozenOffsets,
      zebraStripe,
      lastColumnWidth,
      minWidth,
      condensed,
      dataDensity,
      callToActionColumnIndex: t1,
      callToActionHighlightColor: t2
    } = _t);
    rest = _objectWithoutPropertiesLoose(_t, _excluded);
    _t;
    $[0] = t0;
    $[1] = children;
    $[2] = condensed;
    $[3] = dataDensity;
    $[4] = frozenOffsets;
    $[5] = lastColumnWidth;
    $[6] = minWidth;
    $[7] = rest;
    $[8] = t1;
    $[9] = t2;
    $[10] = zebraStripe;
  } else {
    children = $[1];
    condensed = $[2];
    dataDensity = $[3];
    frozenOffsets = $[4];
    lastColumnWidth = $[5];
    minWidth = $[6];
    rest = $[7];
    t1 = $[8];
    t2 = $[9];
    zebraStripe = $[10];
  }
  const callToActionColumnIndex = t1 === undefined ? null : t1;
  const callToActionHighlightColor = t2 === undefined ? null : t2;
  let t3;
  if ($[11] !== children || $[12] !== condensed || $[13] !== rest) {
    t3 = /*#__PURE__*/_jsx(Table, Object.assign({
      condensed: condensed
    }, rest, {
      children: children
    }));
    $[11] = children;
    $[12] = condensed;
    $[13] = rest;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== callToActionColumnIndex || $[16] !== callToActionHighlightColor || $[17] !== condensed || $[18] !== dataDensity || $[19] !== frozenOffsets || $[20] !== lastColumnWidth || $[21] !== minWidth || $[22] !== t3 || $[23] !== zebraStripe) {
    t4 = /*#__PURE__*/_jsx(TableContainer, {
      $condensed: condensed,
      $dataDensity: dataDensity,
      frozenOffsets: frozenOffsets,
      lastColumnWidth: lastColumnWidth,
      minWidth: minWidth,
      zebraStripe: zebraStripe,
      $callToActionColumnIndex: callToActionColumnIndex,
      $callToActionHighlightColor: callToActionHighlightColor,
      children: t3
    });
    $[15] = callToActionColumnIndex;
    $[16] = callToActionHighlightColor;
    $[17] = condensed;
    $[18] = dataDensity;
    $[19] = frozenOffsets;
    $[20] = lastColumnWidth;
    $[21] = minWidth;
    $[22] = t3;
    $[23] = zebraStripe;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  return t4;
};
export const NoFirstDivChildPadding = styled.div.withConfig({
  displayName: "StyledTableComponents__NoFirstDivChildPadding"
})(["& > div:first-child{padding:0;}"]);